import React, { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { testimonials } from "../data/catalog";

const initials = (name) =>
  name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const TestimonialCard = ({ t, idx }) => (
  <div
    data-testid={`testi-card-${idx}`}
    className="product-card p-6 sm:p-7 h-full flex flex-col"
  >
    <Quote className="text-[#7B2DFF]/70" size={28} />
    <p className="mt-4 text-white/80 text-sm sm:text-base leading-[1.75] flex-1">
      "{t.text}"
    </p>
    <div className="mt-6 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#7B2DFF] to-[#3a0ca3] flex items-center justify-center font-display font-bold text-sm">
        {initials(t.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm">
          {t.name} <span className="text-white/40 font-normal">({t.age})</span>
        </div>
        <div className="text-white/45 text-[11px] uppercase tracking-[0.14em]">
          {t.city}
        </div>
      </div>
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={12} className="fill-[#9B5BFF] text-[#9B5BFF]" />
        ))}
      </div>
    </div>
  </div>
);

const Testimonials = () => {
  const [emblaRef, embla] = useEmblaCarousel({
    loop: true,
    align: "start",
    dragFree: false,
  });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
    const id = setInterval(() => {
      if (embla) embla.scrollNext();
    }, 4500);
    return () => {
      clearInterval(id);
      embla.off("select", onSelect);
    };
  }, [embla, onSelect]);

  return (
    <section
      id="testimoni"
      data-testid="testimonials-section"
      className="relative py-24 sm:py-28"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0d0717] to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="text-xs tracking-[0.22em] uppercase text-[#9B5BFF] font-medium">
              — Testimoni Pelanggan
            </div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-4 leading-[1.05] max-w-2xl">
              Cerita Mereka,{" "}
              <span className="gradient-purple">Bukti Pelayanan Kami.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="testi-prev"
              aria-label="Previous testimonial"
              onClick={() => embla?.scrollPrev()}
              className="w-11 h-11 rounded-full glass hover:border-[#7B2DFF]/50 hover:text-[#cdb6ff] transition flex items-center justify-center"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="testi-next"
              aria-label="Next testimonial"
              onClick={() => embla?.scrollNext()}
              className="w-11 h-11 rounded-full glass hover:border-[#7B2DFF]/50 hover:text-[#cdb6ff] transition flex items-center justify-center"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="mt-12 overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.3333%] pr-5 sm:pr-6"
              >
                <TestimonialCard t={t} idx={i} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              data-testid={`testi-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              onClick={() => embla?.scrollTo(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                selected === i
                  ? "w-8 bg-[#9B5BFF]"
                  : "w-1.5 bg-white/15 hover:bg-white/30"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
